import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, ArrowRight, ChevronDown, Disc3, ExternalLink } from "lucide-react";
import { MaskedLine, FadeIn, SectionHeading } from "@/components/Reveal";
import { PlayableCover, WatchOnYouTube } from "@/components/ReleasePlayer";
import Marquee from "@/components/Marquee";
import { IMAGES } from "@/data/content";
import { useSite } from "@/context/SiteSettings";
import { ytThumb } from "@/lib/youtube";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Waveform = () => (
    <div className="flex items-end gap-1 h-6" aria-hidden="true">
        {[0.5, 0.9, 0.4, 1, 0.6, 0.85, 0.35].map((d, i) => (
            <span
                key={i}
                className="wave-bar w-[3px] h-full rounded-full bg-gradient-to-t from-amber-600 to-amber-300"
                style={{ animationDelay: `${i * 0.12}s`, animationDuration: `${1 + d * 0.5}s` }}
            />
        ))}
    </div>
);

const Hero = ({ hasVideos, hasFeatured }) => {
    const { t } = useSite();
    const listenNow = (e) => {
        if (!hasFeatured) return;
        e.preventDefault();
        const target = document.getElementById("latest-release");
        if (!target) return;
        if (window.__lenis) window.__lenis.scrollTo(target, { offset: -70 });
        else target.scrollIntoView({ behavior: "smooth" });
    };

    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

    return (
        <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex items-center overflow-hidden grain">
            <motion.div style={{ y: bgY }} className="absolute inset-0">
                <motion.img
                    src={IMAGES.hero}
                    alt="Hands raised in worship at a packed night show"
                    initial={{ scale: 1.15, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
                    className="w-full h-[120%] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/55 to-[#0A0A0E]" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(200,151,43,0.14),transparent_60%)]" />
            </motion.div>

            <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-28 pb-20 w-full">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35, duration: 0.7 }}
                    className="inline-flex items-center gap-3 border border-amber-500/30 rounded-full px-4 py-2 bg-black/40 backdrop-blur-sm"
                >
                    <Waveform />
                    <span className="font-meta text-[10px] sm:text-[11px] tracking-[0.3em] uppercase text-amber-300">
                        Faith-Based Urban Artist
                    </span>
                </motion.div>

                <h1 className="font-display font-extrabold uppercase tracking-tight leading-[0.92] mt-6 text-[12.5vw] sm:text-[13vw] lg:text-[9.5rem]">
                    <MaskedLine delay={0.5}>
                        <span className="text-white">{t("home_word_1")}</span>
                        <span className="text-gold-gradient">{t("home_word_2")}</span>
                    </MaskedLine>
                    <MaskedLine delay={0.68}>
                        <span className="text-white">{t("home_word_3")}</span>
                    </MaskedLine>
                </h1>
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.05, duration: 0.8 }}
                    className="mt-6 text-base sm:text-lg text-zinc-300 max-w-xl leading-relaxed"
                >
                    {t("home_tagline")}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.25, duration: 0.8 }}
                    className="mt-9 flex flex-wrap items-center gap-4"
                >
                    <Link
                        to="/music"
                        onClick={listenNow}
                        data-testid="hero-listen-now-button"
                        className="inline-flex items-center gap-3 bg-gradient-to-r from-amber-500 via-amber-400 to-amber-600 text-black font-bold px-8 py-4 rounded-full text-sm sm:text-base hover:shadow-[0_0_35px_rgba(200,151,43,0.45)] hover:scale-105 transition-all duration-200"
                    >
                        <Play size={18} fill="currentColor" />
                        Listen Now
                    </Link>
                    {hasVideos && (
                        <Link
                            to="/videos"
                            data-testid="hero-watch-video-button"
                            className="inline-flex items-center gap-3 bg-zinc-900/70 backdrop-blur text-amber-200 border border-amber-500/40 px-8 py-4 rounded-full text-sm sm:text-base hover:bg-amber-500/10 transition-all duration-200"
                        >
                            Watch Latest Video
                            <ArrowRight size={16} />
                        </Link>
                    )}
                </motion.div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.8 }}
                className="absolute bottom-7 left-1/2 -translate-x-1/2 text-amber-400/70 z-10"
            >
                <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
                    <ChevronDown size={26} />
                </motion.div>
            </motion.div>
        </section>
    );
};

const FeaturedRelease = ({ release }) => {
    const [playing, setPlaying] = useState(false);

    if (!release) return null;
    const { links, slug } = release;

    return (
        <section id="latest-release" data-testid="featured-release-section" className="relative py-24 sm:py-32 px-5 sm:px-8 overflow-hidden">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[70vw] h-[40vh] bg-amber-500/10 blur-[120px] rounded-full pointer-events-none" />
            <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
                <FadeIn className="relative">
                    <div className="absolute -inset-6 bg-[radial-gradient(circle_at_50%_0%,rgba(200,151,43,0.22),transparent_65%)] pointer-events-none" />
                    <div className="relative gold-ring rounded-2xl overflow-hidden max-w-md mx-auto">
                        <motion.div
                            initial={{ clipPath: "inset(100% 0 0 0)" }}
                            whileInView={{ clipPath: "inset(0% 0 0 0)" }}
                            viewport={{ once: true }}
                            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                            className="relative aspect-square"
                        >
                            <PlayableCover release={release} open={playing} onToggle={() => setPlaying((p) => !p)} />
                        </motion.div>
                    </div>
                    {playing && (
                        <div className="max-w-md mx-auto">
                            <WatchOnYouTube release={release} />
                        </div>
                    )}
                </FadeIn>
                <div>
                    <SectionHeading eyebrow="Latest Release" title={release.title} />
                    <FadeIn delay={0.1}>
                        <p className="mt-5 font-meta text-[11px] tracking-[0.3em] uppercase text-zinc-500">
                            {release.type}
                            {release.tracks ? ` · ${release.tracks} tracks` : ""} · {release.status}
                        </p>
                        {release.description && (
                            <p className="mt-4 text-zinc-300 leading-relaxed" data-testid="featured-release-description">
                                {release.description}
                            </p>
                        )}
                    </FadeIn>
                    <FadeIn delay={0.15}>
                        <div className="flex flex-wrap gap-2 mt-6">
                            {links.spotify && (
                                <a href={links.spotify} target="_blank" rel="noopener noreferrer" data-testid={`featured-spotify-link-${slug}`} className="inline-flex items-center gap-1.5 text-[11px] font-meta tracking-wider uppercase text-amber-200/90 border border-amber-500/25 rounded-full px-3 py-1.5 hover:bg-amber-500 hover:text-black transition-all duration-200">
                                    Spotify <ExternalLink size={11} />
                                </a>
                            )}
                            {links.apple && (
                                <a href={links.apple} target="_blank" rel="noopener noreferrer" data-testid={`featured-apple-link-${slug}`} className="inline-flex items-center gap-1.5 text-[11px] font-meta tracking-wider uppercase text-amber-200/90 border border-amber-500/25 rounded-full px-3 py-1.5 hover:bg-amber-500 hover:text-black transition-all duration-200">
                                    Apple Music <ExternalLink size={11} />
                                </a>
                            )}
                            {links.amazon && (
                                <a href={links.amazon} target="_blank" rel="noopener noreferrer" data-testid={`featured-amazon-link-${slug}`} className="inline-flex items-center gap-1.5 text-[11px] font-meta tracking-wider uppercase text-amber-200/90 border border-amber-500/25 rounded-full px-3 py-1.5 hover:bg-amber-500 hover:text-black transition-all duration-200">
                                    Amazon <ExternalLink size={11} />
                                </a>
                            )}
                        </div>
                    </FadeIn>
                    <FadeIn delay={0.2} className="mt-8">
                        <Link
                            to="/music"
                            data-testid="featured-release-cta"
                            className="inline-flex items-center gap-3 bg-gradient-to-r from-amber-500 via-amber-400 to-amber-600 text-black font-bold px-7 py-3.5 rounded-full text-sm hover:shadow-[0_0_30px_rgba(200,151,43,0.4)] hover:scale-105 transition-all duration-200"
                        >
                            <Disc3 size={17} />
                            Explore the Discography
                        </Link>
                    </FadeIn>
                </div>
            </div>
        </section>
    );
};

const VideosTeaser = ({ videos }) => {
    if (videos.length === 0) return null;

    return (
        <section data-testid="videos-teaser-section" className="py-24 sm:py-32 px-5 sm:px-8 bg-[#0D0D13]">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
                    <SectionHeading eyebrow="On Screen" title="Watch the Movement" />
                    <FadeIn delay={0.15}>
                        <Link
                            to="/videos"
                            data-testid="videos-teaser-link"
                            className="inline-flex items-center gap-2 text-amber-300 font-meta text-[11px] tracking-[0.25em] uppercase hover:gap-4 transition-all duration-300"
                        >
                            All Videos <ArrowRight size={15} />
                        </Link>
                    </FadeIn>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                    {videos.slice(0, 2).map((v, i) => (
                        <FadeIn key={v.id} delay={i * 0.12}>
                            <Link
                                to="/videos"
                                data-testid={`videos-teaser-card-${v.id}`}
                                className="group relative block rounded-2xl overflow-hidden border border-amber-500/15 hover:border-amber-400/50 transition-all duration-300"
                            >
                                <img
                                    src={ytThumb(v.youtube_id)}
                                    alt={v.title}
                                    loading="lazy"
                                    className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <span className="w-16 h-16 rounded-full bg-amber-500/90 text-black flex items-center justify-center opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100 transition-all duration-300 shadow-[0_0_30px_rgba(200,151,43,0.5)]">
                                        <Play size={24} fill="currentColor" />
                                    </span>
                                </div>
                                <div className="absolute bottom-0 inset-x-0 p-5">
                                    {v.caption && (
                                        <p className="font-meta text-[10px] tracking-[0.3em] uppercase text-amber-400">{v.caption}</p>
                                    )}
                                    <p className="font-display font-semibold text-white mt-1">{v.title}</p>
                                </div>
                            </Link>
                        </FadeIn>
                    ))}
                </div>
            </div>
        </section>
    );
};

const AboutTeaser = () => (
    <section data-testid="about-teaser-section" className="py-24 sm:py-32 px-5 sm:px-8 relative overflow-hidden">
        <div className="absolute top-1/2 -left-32 w-[55vw] h-[45vh] -translate-y-1/2 bg-amber-500/[0.07] blur-[140px] rounded-full pointer-events-none" />
        <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-10 md:gap-16 items-start">
            <div className="md:col-span-5">
                <SectionHeading eyebrow="The Story" title="More Than Music. A Mission." />
            </div>
            <div className="md:col-span-7 md:pt-4">
                <FadeIn delay={0.1}>
                    <p className="font-display text-xl sm:text-2xl font-semibold text-white leading-snug">
                        Testimony turned into sound.
                    </p>
                </FadeIn>
                <FadeIn delay={0.16}>
                    <p className="mt-5 text-zinc-300 leading-relaxed max-w-xl">
                        Trap drums, worship melodies, and unfiltered faith — anthems written for a generation searching
                        for something real, and built to carry light into every room they play in.
                    </p>
                </FadeIn>
                <FadeIn delay={0.22} className="mt-8 flex items-center gap-6">
                    <span className="h-px flex-1 max-w-[120px] bg-gradient-to-r from-amber-500/60 to-transparent" />
                    <Link
                        to="/about"
                        data-testid="about-teaser-link"
                        className="inline-flex items-center gap-2 text-amber-300 font-meta text-[11px] tracking-[0.25em] uppercase hover:gap-4 transition-all duration-300"
                    >
                        Read the Full Story <ArrowRight size={15} />
                    </Link>
                </FadeIn>
            </div>
        </div>
    </section>
);

const Home = () => {
    const [videos, setVideos] = useState([]);
    const [featured, setFeatured] = useState(null);

    useEffect(() => {
        axios
            .get(`${API}/videos`)
            .then((res) => setVideos(res.data))
            .catch(() => {});
        axios
            .get(`${API}/releases/featured`)
            .then((res) => setFeatured(res.data))
            .catch(() => {});
    }, []);

    return (
        <div data-testid="home-page">
            <Hero hasVideos={videos.length > 0} hasFeatured={featured !== null} />
            <Marquee />
            <FeaturedRelease release={featured} />
            <VideosTeaser videos={videos} />
            <AboutTeaser />
        </div>
    );
};

export default Home;
